import { match } from 'ts-pattern'
import { type SerializedPageStructure } from '@wix/document-services-types'
import { entries } from '@wix/wix-data-client-common-standalone'
import { FieldType, type Schema } from '@wix/wix-data-schema-types'

import { applyModifiers } from '../utils'
import { addComponentForAddressField } from './addComponentForAddressField'
import { addComponentForBooleanField } from './addComponentForBooleanField'
import { addComponentForDateField } from './addComponentForDateField'
import { addComponentForDateTimeField } from './addComponentForDateTimeField'
import { addComponentForImageField } from './addComponentForImageField'
import { addComponentForNumberField } from './addComponentForNumberField'
import { addComponentForRichTextField } from './addComponentForRichTextField'
import { addComponentForTextField } from './addComponentForTextField'
import { addComponentForTimeField } from './addComponentForTimeField'
import { addComponentForUrlField } from './addComponentForUrlField'
import { breakDownFieldTemplates } from './breakDownFieldTemplates'
import { normalizeLastItemMarginInFieldStack } from './normalizeLastItemMarginInFieldStack'
import { removeFieldTemplates } from './removeFieldTemplates'
import { removeRouterDatasetConnectionsToComponents } from './removeRouterDatasetConnectionsToComponents'

export const addComponentsForFields = ({
  page,
  fields,
}: {
  page: SerializedPageStructure
  fields: Schema['fields']
}) => {
  const {
    templateComponentByVeloId,
    templateControllerConnectionsByVeloId,
    connectedTemplateComponentIds,
  } = breakDownFieldTemplates(page)

  return applyModifiers({
    target: page,
    modifiers: {
      removeFieldTemplates,

      removeFieldTemplateControllerConnections: page =>
        removeRouterDatasetConnectionsToComponents({
          page,
          componentIds: connectedTemplateComponentIds,
        }),

      ...entries(fields).reduce<{
        [name: string]: (
          page: SerializedPageStructure,
        ) => SerializedPageStructure
      }>((pageModifiers, [fieldKey, field]) => {
        if (field.systemField || field.isDeleted) {
          return pageModifiers
        }

        const fieldComponentAdder = match(field.type)
          .with(FieldType.address, () => addComponentForAddressField)
          .with(FieldType.boolean, () => addComponentForBooleanField)
          .with(FieldType.date, () => addComponentForDateField)
          .with(FieldType.dateTime, () => addComponentForDateTimeField)
          .with(FieldType.image, () => addComponentForImageField)
          .with(FieldType.number, () => addComponentForNumberField)
          .with(FieldType.richText, () => addComponentForRichTextField)
          .with(FieldType.text, () => addComponentForTextField)
          .with(FieldType.time, () => addComponentForTimeField)
          .with(FieldType.url, () => addComponentForUrlField)
          .otherwise(() => null)

        return fieldComponentAdder !== null
          ? {
              ...pageModifiers,
              [`addComponentForField-${fieldKey}`]: page =>
                fieldComponentAdder({
                  fieldKey,
                  fieldName: field.displayName,
                  page,
                  templateComponentByVeloId,
                  templateControllerConnectionsByVeloId,
                }),
            }
          : pageModifiers
      }, {}),

      normalizeLastItemMarginInFieldStack,
    },
  })
}
